import { Select } from "@package/subframe/components/Select";

import {
  memberDisplayName as displayName,
  type NurseryMemberOption as MemberOption,
} from "@/lib/member-display";

import type { FieldControlProps } from "./EventRecordForm";
import type { DerivedTimingRow } from "./fire-drill-timings";

/**
 * Fire-drill-specific UI behaviour layered on top of the generic Event Records
 * framework (NAU-146). The framework renderer stays type-driven and generic;
 * everything bespoke to the fire-drill event type lives here so NAU-27/28
 * (parent complaint / incident) reuse the framework without inheriting this.
 *
 * Three fire-drill quirks live here (NAU-13 follow-up):
 *  - Time fields capture seconds (the generic `time` renderer is minute-only).
 *  - "Responsible person" is a dropdown of the nursery's users (stored as a
 *    name snapshot — the record is immutable, so we don't want a dangling FK).
 *  - Two derived, display-only timings are shown but never stored. The maths
 *    lives in `fire-drill-timings.ts` (pure + unit-tested); re-exported here so
 *    callers keep a single `@/components/event-records/fire-drill` import.
 */

export {
  type DerivedTimingRow,
  deriveTimingRows,
  formatDuration,
} from "./fire-drill-timings";

// Defined in the monitoring lib beside the other slugs the Monitoring page
// matches on, and re-exported here so callers keep one fire-drill import.
export { FIRE_DRILL_SLUG } from "@/lib/monitoring";

/** Schema key whose value is the chosen responsible person's name. */
export const RESPONSIBLE_PERSON_KEY = "personInCharge";

/** Time fields render with seconds precision on the fire-drill form. */
export const TIME_STEP_SECONDS = 1;

// Generic to nursery members rather than fire-drill-specific, so it lives in
// lib/ and is re-exported here to keep existing imports working. A re-export does
// not bind the name locally, hence the aliased import for use below.
export {
  memberDisplayName,
  type NurseryMemberOption,
} from "@/lib/member-display";

/**
 * "Responsible person" control — a dropdown of nursery users that stores the
 * chosen user's display name (not their id). Drop-in replacement for the
 * generic text field, supplied to EventRecordForm via `fieldRenderers`.
 */
export function ResponsiblePersonSelect({
  field,
  value,
  error,
  onChange,
  members,
  isLoading,
}: FieldControlProps & {
  members: MemberOption[];
  isLoading?: boolean;
}) {
  const label = `${field.label}${field.required ? " *" : ""}`;
  return (
    <Select
      className="h-auto w-full flex-none"
      label={label}
      placeholder={isLoading ? "Loading team…" : "Select a person…"}
      disabled={isLoading}
      error={!!error}
      helpText={error}
      value={(value as string) || undefined}
      onValueChange={(v) => onChange(v)}
    >
      {members.map((member) => {
        const name = displayName(member);
        return (
          <Select.Item key={member.userId} value={name}>
            {name}
          </Select.Item>
        );
      })}
    </Select>
  );
}

/** Read-only card of the derived timings, shown live beneath the form fields. */
export function DerivedTimingRows({ rows }: { rows: DerivedTimingRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex w-full flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center justify-between gap-3">
          <span className="text-body-bold font-body-bold text-default-font">
            {row.label}
          </span>
          <span className="text-body font-body text-subtext-color">
            {row.value ?? "—"}
          </span>
        </div>
      ))}
    </div>
  );
}
